import { AssociationWorkingHoursEntry } from "../types/association.types";
import { WorkingHoursEntry } from "../types/shared";
import {
  ASSOCIATION_NOT_ADMINISTERED_ERROR,
  ASSOCIATION_SAVE_ERROR,
} from "./associationErrors";

// Association portal, slice 3: opening hours.
//
// The association schedule speaks `openFrom` / `openUntil` in BOTH directions,
// strictly "HH:mm"; the shared WorkingHoursEditor speaks the advisor's
// `availableFrom` / `availableUntil`. The two mappers below rename the fields
// and nothing else: no ordering, no filtering, no time rewriting. The backend
// performs no ordering check and silently drops rows without a `dayOfWeek`,
// so the editor's strict `from < until` validation is the only guard and
// stays in charge.

export const toEditorEntries = (
  entries: AssociationWorkingHoursEntry[],
): WorkingHoursEntry[] =>
  entries.map(({ dayOfWeek, openFrom, openUntil }) => ({
    dayOfWeek,
    availableFrom: openFrom,
    availableUntil: openUntil,
  }));

export const toAssociationEntries = (
  entries: WorkingHoursEntry[],
): AssociationWorkingHoursEntry[] =>
  entries.map(({ dayOfWeek, availableFrom, availableUntil }) => ({
    dayOfWeek,
    openFrom: availableFrom,
    openUntil: availableUntil,
  }));

export const HOURS_SAVED_NOTICE = "Radno vreme je sačuvano.";
export const HOURS_REJECTED_ERROR =
  "Radno vreme nije prihvaćeno. Proverite unos i pokušajte ponovo.";

/** The shape this codebase's axios rejections actually have. */
type RejectionLike = {
  response?: { status?: number; data?: unknown };
};

const responseOf = (error: unknown): RejectionLike["response"] =>
  typeof error === "object" && error !== null
    ? (error as RejectionLike).response
    : undefined;

const errorCodeOf = (error: unknown): string | undefined => {
  const data = responseOf(error)?.data;
  if (data && typeof data === "object") {
    const code = (data as Record<string, unknown>).errorCode;
    return typeof code === "string" ? code : undefined;
  }
  return undefined;
};

/** Backend `AssociationErrorCode.ASSOCIATION_NOT_ADMINISTERED`, always a 400. */
export const isNotAdministeredError = (error: unknown): boolean =>
  responseOf(error)?.status === 400 &&
  errorCodeOf(error) === "ASSOCIATION_NOT_ADMINISTERED";

/**
 * The hours PUT has no bean validation (`@Valid` is absent), so a bad body
 * (e.g. an unparseable time) is a 400 with an EMPTY body: the "not accepted"
 * copy. A 400 `{errorCode, message}` body: the ownership copy for
 * ASSOCIATION_NOT_ADMINISTERED, otherwise the "not accepted" copy too (the
 * backend's messages are English and never shown). Anything else: the
 * generic save failure.
 */
export const mapWorkingHoursError = (error: unknown): string => {
  if (responseOf(error)?.status !== 400) {
    return ASSOCIATION_SAVE_ERROR;
  }
  if (errorCodeOf(error) === "ASSOCIATION_NOT_ADMINISTERED") {
    // Plan 1.3: the administrator pairing no longer holds; the caller
    // re-reads `/mine`.
    return ASSOCIATION_NOT_ADMINISTERED_ERROR;
  }
  return HOURS_REJECTED_ERROR;
};
